import {transparentize} from 'polished';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {PaletteColorsEnum} from '../../../../../libs/shared/core/src/models/api/paletteModel';
import {blendColors} from '../../../../../libs/shared/core/src/models/helpers/colorHelpers';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {isDarkTheme} from '../../../../../libs/shared/ui/src/theme/themeTypeHelpers';

/*
 * Props.
 */

interface ArticleLinkButtonProps {
  name: string;
  onClick: () => void;
}

/*
 * Styles.
 */

const StyledButtonContentDiv = styled.div`
  display: flex;
  flex-direction: row;
  gap: 5px;
  border-radius: 8px;
  padding: 8px;
  border: 2px solid ${(p) => p.theme.alphas.black20};
  align-items: center;
  line-height: 1.5;

  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.bold};
  color: ${(p) => p.theme.greys.shade80};
  background: ${(p) => p.theme.button.foreground};

  &:hover {
    color: ${(p) => p.theme.palette[PaletteColorsEnum.BLUE].shade40};

    background-color: ${(p) =>
      isDarkTheme(p.theme)
        ? transparentize(0.92, p.theme.greys.black)
        : blendColors(p.theme.foreground, p.theme.alphas.gray10)};
  }
`;

const StyledNameDiv = styled(TooltipOverflowCondition)`
  flex-grow: 1;
`;

/*
 * Component.
 */

export const ArticleLinkButton: FC<ArticleLinkButtonProps> = (props) => {
  const {name, onClick} = props;

  const [hasHover, setHasHover] = useState(false);

  return (
    <TooltipCoordinator render={() => name}>
      <ChromelessButton
        onClick={onClick}
        onMouseEnter={() => setHasHover(true)}
        onMouseLeave={() => setHasHover(false)}
      >
        <StyledButtonContentDiv>
          <Icon name="knowledgeBase" />
          <StyledNameDiv>{name}</StyledNameDiv>
          {hasHover && <Icon name="externalLink" />}
        </StyledButtonContentDiv>
      </ChromelessButton>
    </TooltipCoordinator>
  );
};
